/**
 * snapshotBuild — one portal snapshot per subcontractor, from the ledgers staff already read.
 *
 * PURE. The publisher (portalPublisher.ts) reads the lists; this decides what each sewer is
 * shown. Everything is DISPLAY: BQC4's receiving scans and BQC5's passes stay the records, and
 * nothing here writes a list. A payout travels without bank details — the sewer prints their own
 * payout, not their account number — and the API drops anything not named again on arrival
 * (api/src/shared/sewerPortal.ts cleanSnapshot).
 */
import type { ISlip } from "../../lib/sp/DispatchService";
import type { IPayout } from "../subcontractors/payout";
import type { IQcScan } from "../mfg-workcenters/qcLedger";
import type { IReceivingScan } from "../mfg-workcenters/receivingLedger";
import { groupByWip, qcTallyIn, receiveTallyIn } from "../mfg-workcenters/qcIndex";
import { canonKey } from "../mfg-workcenters/subcontractors";

export interface IPortalWipInput { wipId: string; qty: number; sku?: string }

export interface IBuiltWip {
  wipId: string; sku: string; qty: number; ds?: string; grn?: string;
  received: number; passed: number; failed: number; paid: number;
}
export interface IBuiltSnapshot {
  ds: { dsNo: string; issuedAt: string; wipIds: string[] }[];
  wips: IBuiltWip[];
  payouts: { ref: string; periodEnd: string; total: number; sets: number; status: string; doc: unknown }[];
  perf?: { grade: string; firstPassPct?: number; period?: string };
}

/** How far back a finished DS is still shown. Open WIPs are shown whatever their age. */
export const DS_WINDOW_DAYS = 60;
const DAY = 86_400_000;

/** A payout as the sewer may see it: the whole record, minus where the money went. */
export function withoutBankDetails(p: IPayout): IPayout {
  return { ...p, bankName: undefined, bankAccount: undefined, tin: undefined, schedPayout: undefined };
}

export function gradeOf(firstPassPct: number): string {
  return firstPassPct >= 97 ? "A" : firstPassPct >= 92 ? "B" : firstPassPct >= 85 ? "C" : "D";
}

export function buildSnapshots(input: {
  slips: readonly ISlip[];
  wips: readonly IPortalWipInput[];
  receiving: readonly IReceivingScan[];
  qc: readonly IQcScan[];
  /** The record and the total the list stored for it (never re-summed from lines). */
  payouts: readonly { payout: IPayout; total: number }[];
  now: Date;
}): Map<string, IBuiltSnapshot> {
  const wipById = new Map(input.wips.map((w) => [w.wipId, w]));
  const rIdx = groupByWip(input.receiving);
  const qIdx = groupByWip(input.qc);
  const cutoff = input.now.getTime() - DS_WINDOW_DAYS * DAY;

  // Latest live slip names the sewer, exactly as payroll does it.
  const best = new Map<string, { at: string; slip: ISlip }>();
  for (const s of input.slips) {
    if (s.slipStatus === "Cancelled" || s.historical) continue;
    for (const raw of s.wipIds ?? []) {
      const id = String(raw ?? "").trim();
      const cur = best.get(id);
      if (id && (!cur || (s.generatedAt ?? "") >= cur.at)) best.set(id, { at: s.generatedAt ?? "", slip: s });
    }
  }

  const paidSets = new Map<string, number>();
  for (const { payout: p } of input.payouts) {
    if (p.status !== "Paid") continue;
    for (const l of p.lines) for (const ps of l.passes ?? []) paidSets.set(ps.wipId, (paidSets.get(ps.wipId) ?? 0) + ps.sets);
  }

  const out = new Map<string, IBuiltSnapshot>();
  const snap = (key: string): IBuiltSnapshot => {
    let s = out.get(key);
    if (!s) { s = { ds: [], wips: [], payouts: [] }; out.set(key, s); }
    return s;
  };

  const seenDs = new Set<string>();
  for (const [wipId, { slip }] of best) {
    const key = canonKey(slip.sewer);
    const w = wipById.get(wipId);
    if (!key || !w) continue;
    const r = receiveTallyIn(rIdx, wipId, w.qty);
    const q = qcTallyIn(qIdx, wipId, w.qty);
    const paid = Math.min(paidSets.get(wipId) ?? 0, w.qty);
    const open = r.received < w.qty || q.passed < w.qty || q.repair > 0;
    const recent = Date.parse(slip.generatedAt ?? "") >= cutoff;
    if (!open && !recent) continue;
    snap(key).wips.push({
      wipId, sku: w.sku ?? "", qty: w.qty, ds: slip.dsNumber, grn: slip.grn,
      received: r.received, passed: q.passed, failed: q.repair, paid,
    });
    const dsKey = `${key}|${slip.dsNumber}`;
    if (!seenDs.has(dsKey)) {
      seenDs.add(dsKey);
      snap(key).ds.push({ dsNo: slip.dsNumber, issuedAt: slip.generatedAt ?? "", wipIds: [] });
    }
    snap(key).ds.find((d) => d.dsNo === slip.dsNumber)!.wipIds.push(wipId);
  }

  for (const { payout: p, total } of input.payouts) {
    if (p.status === "Void") continue;
    const key = canonKey(p.sewer);
    if (!key) continue;
    const sets = p.lines.reduce((n, l) => n + (l.qty ?? 0), 0);
    snap(key).payouts.push({
      ref: p.payoutRef, periodEnd: p.periodEnd, total, sets, status: p.status, doc: withoutBankDetails(p),
    });
  }

  for (const [key, s] of out) {
    s.wips.sort((a, b) => b.wipId.localeCompare(a.wipId));
    s.ds.sort((a, b) => b.issuedAt.localeCompare(a.issuedAt));
    s.payouts.sort((a, b) => b.periodEnd.localeCompare(a.periodEnd));
    const passed = s.wips.reduce((n, w) => n + w.passed, 0);
    const failed = s.wips.reduce((n, w) => n + w.failed, 0);
    if (passed + failed > 0) {
      const pct = Math.round((passed / (passed + failed)) * 1000) / 10;
      s.perf = { grade: gradeOf(pct), firstPassPct: pct, period: `last ${DS_WINDOW_DAYS} days` };
    }
    void key;
  }
  return out;
}
